import { useAccountScope } from '../context/AccountScope';
import { useEffect, useRef, useState } from 'react';
import isMobileLayout from '../utils/isMobileLayout';
import { activarNotificacionesPush, desvincularNotificacionesPush } from '../services/pushNotifications';
import './ConfigurationPage.css';
import SpeedAlertSettings from '../components/SpeedAlertSettings';
import GpsRestartSettings from '../components/GpsRestartSettings';

const options = [
  ['ignition_on', '🚗', 'Vehículo encendido', 'Aviso cuando se enciende el vehículo.'],
  ['ignition_off', '📍', 'Vehículo apagado', 'Aviso cuando se apaga el vehículo.'],
  ['driver_status', '🛡️', 'Mostrar reconocimiento del conductor', 'Incluye el estado del conductor en el aviso de encendido.'],
  ['centinela', '🚨', 'Alertas Centinela', 'Avisos de seguridad cuando el modo Centinela está activo.'],
  ['geofences', '📌', 'Geocercas', 'Entradas y salidas de las geocercas con avisos configurados.'],
  ['battery', '🔋', 'Batería y alimentación', 'Voltaje bajo, pérdida y recuperación de alimentación.']
];

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function ConfigurationPage() {
  const { fetch, account: scopeAccount, isOwn } = useAccountScope();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const pauseKey = `maccez:push-paused:${user.id}`;
  const [mobile, setMobile] = useState(isMobileLayout);
  const [paused, setPaused] = useState(() => localStorage.getItem(pauseKey) === '1');
  const [permission, setPermission] = useState(() =>
    'Notification' in window ? Notification.permission : 'unsupported');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [selection, setSelection] = useState('all');
  const controllerRef = useRef(null);

  useEffect(() => {
    const update = () => {
      setMobile(isMobileLayout());
      setPermission('Notification' in window ? Notification.permission : 'unsupported');
    };
    window.addEventListener('focus', update);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('focus', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  async function load() {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    try {
      const response = await fetch('/api/notification-preferences', {
        headers: headers(), signal: controller.signal
      });
      if (!response.ok) throw new Error('No se pudieron cargar tus ajustes.');
      const result = await response.json();
      if (!controller.signal.aborted) {
        setData(result); setError('');
        setSelection(current => current === 'all' || result.vehicles.some(vehicle => String(vehicle.id) === current)
          ? current : 'all');
      }
    } catch (cause) {
      if (cause?.name === 'AbortError') return;
      if (!controller.signal.aborted) setError(cause.message);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }

  useEffect(() => {
    load();
    return () => controllerRef.current?.abort();
    // La carga inicial y los cambios se controlan desde esta pantalla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vehicle = data?.vehicles.find(item => String(item.id) === selection);
  const preferences = selection === 'all' ? data?.defaults : vehicle?.preferences;

  async function toggle(key) {
    if (!preferences || saving || loading) return;
    const enabled = !preferences[key];
    setSaving(key); setStatus('');
    try {
      const response = await fetch(`/api/notification-preferences/${selection}`, {
        method: 'PATCH', headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, enabled })
      });
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.message || 'No se pudo guardar el ajuste.');
      }
      await load();
      setStatus('Ajuste guardado en la cuenta seleccionada.');
    } catch (cause) {
      if (cause?.name === 'AbortError') return; setStatus(cause.message); }
    finally { setSaving(''); }
  }

  async function activate() {
    setBusy(true); setStatus('');
    try {
      await activarNotificacionesPush();
      localStorage.removeItem(pauseKey);
      setPaused(false); setPermission(Notification.permission);
      setStatus('Notificaciones activadas en este dispositivo.');
    } catch (cause) {
      if (cause?.name === 'AbortError') return;
      setPermission('Notification' in window ? Notification.permission : 'unsupported');
      setStatus(cause.message || 'No se pudieron activar las notificaciones.');
    } finally { setBusy(false); }
  }

  async function pause() {
    setBusy(true); setStatus('');
    try {
      await desvincularNotificacionesPush();
      localStorage.setItem(pauseKey, '1'); setPaused(true);
      setStatus('Notificaciones pausadas en este dispositivo.');
    } catch (cause) {
      if (cause?.name === 'AbortError') return; setStatus(cause.message || 'No se pudieron pausar las notificaciones.'); }
    finally { setBusy(false); }
  }

  const labels = { granted: 'Permitidas', denied: 'Bloqueadas por el navegador',
    default: 'Pendientes de activar', unsupported: 'No disponibles en este dispositivo' };

  return <main className={`configuration-page ${mobile ? 'is-mobile' : ''}`}>
    <header><small>AJUSTES DE LA APLICACIÓN</small><h1>Configuración</h1><p>{scopeAccount.name}</p>
      <p>Elige los avisos que quieres recibir.</p></header>
    <section>
      <div className="configuration-section-heading"><span>🔔</span><div>
        <h2>Notificaciones de tus activos</h2><p>Estos ajustes se guardan en tu cuenta.</p>
      </div></div>
      <label className="configuration-select-label" htmlFor="notification-vehicle">Aplicar a</label>
      <select id="notification-vehicle" value={selection} disabled={Boolean(saving) || loading}
        onChange={event => { setSelection(event.target.value); setStatus(''); }}>
        <option value="all">Todos los activos de esta cuenta</option>
        {(data?.vehicles || []).map(item => <option key={item.id} value={item.id}>
          {item.name}{item.plate ? ` · ${item.plate}` : ''}
        </option>)}
      </select>
      <p className="configuration-hint">{selection === 'all'
        ? 'Al cambiar una opción aquí, se aplica a todos tus activos, incluidos los nuevos.'
        : 'Personaliza los avisos de este activo.'}</p>
      {loading && !data && <p>Cargando tus ajustes…</p>}
      {error && <div className="configuration-error">{error}
        <button type="button" onClick={load}>Reintentar</button></div>}
      {preferences && <div className="configuration-options">
        {options.map(([key, icon, title, description]) => {
          const blocked = key === 'driver_status' && !preferences.ignition_on;
          return <div className={`configuration-option ${blocked ? 'blocked' : ''}`} key={key}>
            <span className="configuration-option-icon" aria-hidden="true">{icon}</span>
            <div className="configuration-option-text"><strong>{title}</strong>
              <small>{blocked ? 'Activa el aviso de encendido para incluir este estado.' : description}</small></div>
            <button type="button" role="switch" aria-checked={Boolean(preferences[key])}
              aria-label={title} disabled={Boolean(saving) || loading || blocked}
              className={`configuration-switch ${preferences[key] ? 'on' : ''}`}
              onClick={() => toggle(key)}><span /></button>
          </div>;
        })}
      </div>}
      <p className="configuration-hint">Silenciar un aviso conserva su registro en la campanita y en Eventos.
        El sensor y Centinela se activan o desactivan desde Control.</p>
    </section>
    {isOwn && <section>
      <div className="configuration-section-heading"><span>📱</span><div>
        <h2>Este dispositivo</h2><p>Permiso: {labels[permission] || permission}</p>
      </div></div>
      <p>{paused ? 'Los avisos están pausados en este teléfono.' : 'Recibe aquí las categorías activadas en tu cuenta.'}</p>
      {permission === 'granted' && !paused
        ? <button className="configuration-action" type="button" disabled={busy} onClick={pause}>
          {busy ? 'Actualizando…' : 'Pausar en este dispositivo'}</button>
        : permission === 'denied'
          ? <p>Para permitirlas, cambia el permiso desde los ajustes del navegador o del teléfono.</p>
          : <button className="configuration-action" type="button" disabled={busy || permission === 'unsupported'} onClick={activate}>
            {busy ? 'Activando…' : 'Activar en este dispositivo'}</button>}
    </section>}
    <SpeedAlertSettings />
    <GpsRestartSettings />
    {status && <p className="configuration-status" role="status" aria-live="polite">{status}</p>}
  </main>;
}
